import { useState, useMemo } from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import apiClient from '../../services/api/apiClient';
import { CompteUtilisateur } from '../../utils/types/CompteUtilisateur';
import CompteUtilisateurGrid from '../../components/grid/CompteUtilisateurGrid';
import NewUserModal from '../../components/modals/wizard/user/NewUserModal';
import { Helmet } from 'react-helmet-async';
import ClientSelect from '../../components/select/ClientSelect';
import { IClient } from '../../utils/types/IClient';
import { Spinner } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../components/others/QueryErrorAlerts';

const CompteUtilisateurPage = () => {
    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string | null>(null);

    const {
        data: clients = [],
        isLoading: isLoadingClients,
        isFetching: isFetchingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    const {
        data: comptes = [],
        isLoading: isLoadingComptes,
        refetch: refetchComptes,
        isError: isComptesError,
        error: comptesError,
    } = useQuery<CompteUtilisateur[]>({
        queryKey: [
            'comptesUtilisateurs',
            {
                cardCode: selectedCardCode,
                searchTerm,
            },
        ],
        queryFn: async () => {
            const res = await apiClient.get('/api/compteUtilisateur/getData', {
                params: {
                    cardCode: selectedCardCode,
                    searchTerm,
                },
            });
            return res.data ?? [];
        },
        meta: { title: 'Chargement des comptes utilisateurs' },
            staleTime: 1000 * 60 * 10, // 10 min
    });

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients },
            { isError: isComptesError, error: comptesError, title: 'Erreur lors du chargement des comptes utilisateurs', refetch: refetchComptes },
        ],
        [isClientsError, clientsError, refetchClients, isComptesError, comptesError, refetchComptes],
    );

    // ---

    return (
        <>
            <Helmet>
                <title>Comptes Utilisateurs :: Portail PETROSIL</title>
            </Helmet>

            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Listing</div>
                            <h2 className="page-title">Comptes Utilisateurs</h2>
                        </div>

                        <div className="col-auto ms-auto d-print-none">
                            <div className="btn-list">
                                <span className="d-none d-sm-inline">
                                    <button type="button" className="btn btn-1" onClick={() => refetchComptes()}>
                                        Actualiser
                                    </button>
                                </span>

                                <NewUserModal />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="page-body">
                <div className="container-xl">
                    <QueryErrorAlerts queries={queryErrors} />

                    <div className="card">
                        <div className="accordion accordion-flush" id="accordion-flush">
                            <div className="accordion-item">
                                <button className="accordion-header" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-1-flush" aria-expanded="true">
                                    <div className="accordion-header-text">
                                        <h4>Filtre</h4>
                                    </div>
                                    <div className="accordion-header-toggle">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-1">
                                            <path d="M6 9l6 6l6 -6"></path>
                                        </svg>
                                    </div>
                                </button>
                                <div id="collapse-1-flush" className="accordion-collapse collapse show" data-bs-parent="#accordion-flush">
                                    <div className="accordion-body">
                                        <div>
                                            <div className="row">
                                                <div className="col-12 col-sm-6 col-lg-3">
                                                    <label htmlFor="SelectedCardCode">Client *</label>
                                                    <ClientSelect clients={clients} isClientsLoading={isLoadingClients || isFetchingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} />
                                                </div>

                                                <div className="col-12 col-sm-6 col-lg-3">
                                                    <label htmlFor="DateFin">Terme de recherche</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={searchTerm ?? ''}
                                                        onChange={(e) => {
                                                            setSearchTerm(e.target.value);
                                                        }}
                                                        placeholder="Ex.: Nom d'utilisateur, adresse e-mail, etc."
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-group mt-2">
                                                <button type="submit" className="btn btn-default" disabled={isLoadingComptes} onClick={() => refetchComptes()}>
                                                    {isLoadingComptes ? (
                                                        <>
                                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                            &nbsp; Générer
                                                        </>
                                                    ) : (
                                                        'Générer'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-2">
                        <CompteUtilisateurGrid comptes={comptes} />
                    </div>
                </div>
            </div>
        </>
    );
};

CompteUtilisateurPage.Layout = DefaultLayout;

export default CompteUtilisateurPage;
